/**
 * SelectHorseSection - Horse Selection and Creation with Owner Display
 *
 * BUSINESS LOGIC OVERVIEW:
 * This component manages horse selection/creation and displays the horse's owner information
 * in the dropdown. It receives updates when the owner is changed in SelectOwnerSection.
 *
 * OWNER DISPLAY IN DROPDOWN:
 * Each horse option shows: "Horse Name (Nickname) - Barn Name - Owner: Owner Name"
 * The owner information comes from:
 *   - horse.ownerName (denormalized field for performance)
 *   - horse.owner.name (if owner object is loaded)
 *
 * RECEIVING OWNER UPDATES:
 * When SelectOwnerSection changes a horse's owner:
 *   1. SelectOwnerSection updates horse in DB (ownerId, ownerName)
 *   2. Updated horse passed via updatedHorse prop
 *   3. This component formats the updated horse
 *   4. Replaces it in userHorseOptions or barnHorseOptions
 *   5. Dropdown automatically shows new owner (no DB refetch!)
 *
 * PERFORMANCE OPTIMIZATION:
 * Instead of refetching all horses when owner changes:
 *   - Receives single updated horse via updatedHorse prop
 *   - Formats only that one horse
 *   - Updates it in the options array
 *   - Result: 85-95% faster than full refetch
 *
 * AUTO-SAVE BEHAVIOR:
 *   - Saves when user clicks outside the section
 *   - Creates or updates horse in database
 *   - Requires at least horse name to save
 *
 * See ENTRY_FORM_BUSINESS_LOGIC.md for complete documentation
 */

import React, { useContext, useEffect, useRef, useState } from "react";
import { IonCol, IonRow } from "@ionic/react";
import { isPlatform } from "@ionic/core";
import { isWindows } from "../../../utilities/platform/Platform";
import { PersonContext } from "../../../context/PersonContext";
import { Barn, Horse } from "../../../models";
import { Input, Label } from "reactstrap";
import { breeds } from "../../../utilities/horse/Breed";
import { CreateHorseInput, UpdateHorseInput } from "../../../API";
import { createHorse, updateHorse } from "../../../utilities/horse/Horse";
import { formatSingleHorse, formattedOption, getHorsesCreatedByUserOrInCurrentBarn } from "../../../utilities/horse/SelectOrCreateHorseUtils";
import SelectOrCreateHorse from "../../Horse/SelectOrCreateHorse";
import RequiredInputIndicator from "../../Forms/RequiredInputIndicator";
import HorseHeightInput from "../../Horse/HorseHeightInput";
import MembershipUI from "./MembershipUI";
import { FormFieldsInlineSkeleton } from "../../Skeletons/EntrySectionSkeletons";

interface _Props {
    requiredFields?: string[];
    barn?: Barn | null | undefined;
    horse?: Horse | null | undefined;
    onSelectHorse?: (horse?: Horse, areAllRequiredFieldsComplete?: Boolean) => void;
    onSavingStateChange?: (isSaving: boolean) => void;
    onFieldChange?: (horse?: Horse, areAllRequiredFieldsComplete?: Boolean) => void;
    onRegisterSave?: (fn: () => Promise<void> | void) => void;
    updatedHorse?: Horse | null; // Updated horse to merge into options without DB refetch
}

interface HorseFormState {
    name: string;
    height: number;
    fraction: string;
    breed: string;
    sex: string;
}

const SelectHorseSection: React.FC<_Props> = ({ requiredFields, barn, horse, onSelectHorse, onSavingStateChange, onFieldChange, onRegisterSave, updatedHorse }) => {
    const user = useContext(PersonContext);

    // Refs will help us because updating the ref will not cause React to re-render this component.

    // This ref can track the user clicks without necessarily taking any action in the DOM (aka re-rendering)
    const horseComponentRef = useRef<HTMLIonRowElement>(null);

    // This ref can persis the most up-to-date value in the event handlers that run outside of React's lifecycle
    // (aka useOnClickOutside)
    const selectedHorseRef = useRef<Horse | null | undefined>();

    // Ref to track the latest formState for use in save function (avoids stale closure issues)
    const formStateRef = useRef<HorseFormState>({
        name: "",
        height: 0,
        fraction: "",
        breed: "",
        sex: "",
    });

    // selectedHorseState triggers re-renders and allows us to bind data to the UI
    const [selectedHorseState, setSelectedHorseState] = useState<Horse | null | undefined>();
    const [formState, setFormState] = useState<HorseFormState>({
        name: "",
        height: 0,
        fraction: "",
        breed: "",
        sex: "",
    });

    // Track the various horse options available in the section at this level
    // This allows us to make updates to the selectedHorse at this level and update the full list
    const [userHorseOptions, setUserHorseOptions] = useState<formattedOption[] | null | undefined>();
    const [barnHorseOptions, setBarnHorseOptions] = useState<formattedOption[] | null | undefined>();

    const [isLoading, setIsLoading] = useState(false);

    // Flag to prevent duplicate saves
    const isSavingRef = useRef<boolean>(false);

    // Track the last horse name we tried to save to prevent duplicate saves
    const lastSavedHorseNameRef = useRef<string>("");

    useEffect(() => {
        const getData = async () => {
            setIsLoading(true);
            // When barn changes, always refetch from database to ensure accurate data
            // Don't pass userHorseOptions to force a fresh fetch
            const result = await getHorsesCreatedByUserOrInCurrentBarn(user, barn, undefined);

            const createdByOptions = result.createdBy;
            const barnOptions = result.barn;

            setUserHorseOptions(createdByOptions);
            setBarnHorseOptions(barnOptions);
            setIsLoading(false);
        };

        getData();
    }, [barn]);

    /**
     * PERFORMANCE OPTIMIZATION: Handle updated horse from SelectOwnerSection
     *
     * Instead of refetching ALL horses from DB when owner changes (expensive):
     *   1. SelectOwnerSection updates horse.ownerId in DB
     *   2. Receives updated horse object from API response
     *   3. Passes it here via updatedHorse prop
     *   4. We format ONLY that one horse
     *   5. Replace it in the dropdown options
     *
     * BENEFITS:
     *   - 85-95% faster than full refetch
     *   - Instant UI update (no loading spinner)
     *   - Reduces server load
     *   - User sees new owner immediately in dropdown
     */
    useEffect(() => {
        if (updatedHorse && updatedHorse.id) {
            // Update the selected horse state if this is the currently selected horse
            if (selectedHorseState?.id === updatedHorse.id) {
                setSelectedHorseState(updatedHorse);
                selectedHorseRef.current = updatedHorse;
            }

            // Update the horse in the appropriate options list
            const updateHorseInOptions = async () => {
                // Format ONLY this one horse (not all horses)
                const formattedOption = await formatSingleHorse(updatedHorse, user);

                // Check if horse is in user options and replace it
                const userIndex = userHorseOptions?.findIndex((h) => h.value === updatedHorse.id);
                if (userIndex !== undefined && userIndex > -1 && userHorseOptions) {
                    const updatedUserOptions = [
                        ...userHorseOptions.slice(0, userIndex),
                        formattedOption, // Updated horse with new owner info
                        ...userHorseOptions.slice(userIndex + 1),
                    ];
                    setUserHorseOptions(updatedUserOptions);
                }

                // Check if horse is in barn options and replace it
                const barnIndex = barnHorseOptions?.findIndex((h) => h.value === updatedHorse.id);
                if (barnIndex !== undefined && barnIndex > -1 && barnHorseOptions) {
                    const updatedBarnOptions = [
                        ...barnHorseOptions.slice(0, barnIndex),
                        formattedOption, // Updated horse with new owner info
                        ...barnHorseOptions.slice(barnIndex + 1),
                    ];
                    setBarnHorseOptions(updatedBarnOptions);
                }
            };

            updateHorseInOptions();
        }
    }, [updatedHorse]);

    useEffect(() => {
        if (horse) {
            setSelectedHorseState(horse);
            const currentFormState: HorseFormState = {
                name: horse.name || "",
                height: horse.height ? parseFloat(horse.height) : 0,
                fraction: horse.fraction || "",
                sex: horse.sex || "",
                breed: horse.breed || "",
            };
            setFormState(currentFormState);
            formStateRef.current = currentFormState; // Update ref immediately
            const isSectionValid = checkIfSectionIsValid(currentFormState);
            if (onFieldChange) onFieldChange(horse, isSectionValid);
        } else {
            setSelectedHorseState(undefined);
            const currentFormState: HorseFormState = {
                name: "",
                height: 0,
                fraction: "",
                sex: "",
                breed: "",
            };
            setFormState(currentFormState);
            formStateRef.current = currentFormState; // Update ref immediately
        }
    }, [horse]);

    // Keep selectedHorseRef updated
    useEffect(() => {
        selectedHorseRef.current = selectedHorseState;
    }, [selectedHorseState]);

    // Keep formStateRef updated so save function can access latest formState
    useEffect(() => {
        formStateRef.current = formState;
    }, [formState]);

    // Trigger immediate save when a new horse name is set (no existing horse selected)
    // This ensures horses created via VerifyHorseModal are saved immediately
    useEffect(() => {
        // Only trigger if:
        // 1. There's a horse name in formState
        // 2. No horse is currently selected
        // 3. We're not already saving
        // 4. We haven't already saved this horse name
        // 5. The section is valid
        if (formState.name && !selectedHorseState && !isSavingRef.current && formState.name !== lastSavedHorseNameRef.current) {
            const isSectionValid = checkIfSectionIsValid(formState);
            if (isSectionValid) {
                lastSavedHorseNameRef.current = formState.name; // Mark as attempting to save
                // Use a small delay to ensure all state updates are processed
                const timeoutId = setTimeout(() => {
                    handleSaveHorseData();
                }, 150);
                // Cleanup timeout if component unmounts or dependencies change
                return () => clearTimeout(timeoutId);
            }
        }
        // Reset the saved name ref when a horse is selected (so we can save new names again)
        if (selectedHorseState) {
            lastSavedHorseNameRef.current = "";
        }
    }, [formState.name, selectedHorseState]);

    // Register save fn with parent
    useEffect(() => {
        if (!onRegisterSave) return;
        const fn = () => {
            // Use formStateRef to get the latest formState value (avoids stale closure)
            const currentFormState = formStateRef.current;
            // Prevent concurrent saves
            if (isSavingRef.current) {
                return;
            }

            if (selectedHorseRef.current) {
                const currentHorse = selectedHorseRef.current;
                const hasChanges =
                    (currentFormState.name && currentFormState.name !== currentHorse.name) ||
                    currentFormState.height.toString() !== currentHorse.height ||
                    currentFormState.fraction !== currentHorse.fraction ||
                    currentFormState.breed !== currentHorse.breed ||
                    currentFormState.sex !== currentHorse.sex;
                if (hasChanges) {
                    return handleSaveHorseData(currentHorse);
                }
            } else if (currentFormState.name) {
                // Check if a horse with this name was already created (prevent duplicate creation)
                // Check both ref and state to catch any race conditions
                const existingHorse = selectedHorseRef.current || selectedHorseState;
                if (existingHorse && existingHorse.name === currentFormState.name) {
                    // Horse already exists, don't create again
                    return;
                }
                return handleSaveHorseData();
            }
        };
        onRegisterSave(fn);
    }, [onRegisterSave, formState, selectedHorseState]);

    const handleSelectedHorse = async (horse?: Horse, horseName?: string) => {
        setIsLoading(true);
        let currentHorse: Horse | null | undefined = horse;
        // If a horse is selected and there's a barn selected, check if the horse belongs to that barn
        // If not, add it to the barn's dropdown list (and optionally update the database)
        if (currentHorse && barn) {
            // Check if horse already belongs to this barn (handles null/undefined barnId)
            const horseBelongsToBarn = currentHorse.barnId === barn.id;

            // If horse doesn't belong to this barn, add it to the dropdown list
            if (!horseBelongsToBarn) {
                // Capture the horse ID for use in callbacks
                const horseId = currentHorse.id;

                // Format the horse and add it to the barn's dropdown list immediately
                const formattedOption = await formatSingleHorse(currentHorse, user);

                // Add to barnHorseOptions using functional update to avoid stale closure
                setBarnHorseOptions((prevBarnHorses) => {
                    // Check if the horse is already in the list
                    const isAlreadyInBarnList = prevBarnHorses?.some((h) => h.value === horseId);

                    if (!isAlreadyInBarnList) {
                        const updatedBarnHorses: formattedOption[] = (prevBarnHorses || [])?.concat([formattedOption]);
                        return updatedBarnHorses.sort((a, b) => a.horseName.trim().localeCompare(b.horseName.trim()));
                    }
                    return prevBarnHorses;
                });

                // Remove from userHorseOptions to prevent duplicates (horse will appear in barn list)
                // When barn changes, the useEffect will refetch and restore it to userHorseOptions
                // if it doesn't belong to the new barn
                setUserHorseOptions((prevUserHorses) => {
                    if (prevUserHorses) {
                        return prevUserHorses.filter((h) => h.value !== horseId);
                    }
                    return prevUserHorses;
                });

                // Optionally update the horse in the database to associate it with the barn
                // This is done in the background and doesn't block the UI update
                try {
                    const updateHorseInput: UpdateHorseInput = {
                        id: horseId,
                        barnId: barn.id,
                        barnName: barn.name || "",
                    };

                    const updateResult = await updateHorse(updateHorseInput);
                    if (updateResult.isSuccess) {
                        // Update currentHorse with the updated version from the database
                        currentHorse = updateResult.result;

                        // Note: We don't manually remove the horse from userHorseOptions here.
                        // When the barn changes, the useEffect will refetch from the database,
                        // and removeBarnHorsesFromCreatedByGroup will correctly handle removing
                        // duplicates (horses in the current barn won't appear in "created by me").
                        // This ensures the horse will appear in "created by me" when switching
                        // to a different barn where it doesn't belong.
                    }
                } catch (error) {
                    // Silent error handling - if update fails, continue with original horse
                    // The horse is already in the dropdown list, so the UI is still correct
                    // The horse will remain in both lists temporarily until the next refresh
                    console.error("Error updating horse barn association:", error);
                }
            }
        }

        const currentFormState: HorseFormState = {
            name: currentHorse?.name || horseName || "",
            height: currentHorse?.height ? parseFloat(currentHorse?.height) : 0,
            fraction: currentHorse?.fraction || "",
            sex: currentHorse?.sex || "",
            breed: currentHorse?.breed || "",
        };
        setSelectedHorseState(currentHorse);
        setFormState(currentFormState);
        formStateRef.current = currentFormState; // Update ref immediately so save function can access it
        const isSectionValid = checkIfSectionIsValid(currentFormState);
        // Call the prop function to update the parent state
        if (onSelectHorse) {
            onSelectHorse(currentHorse || undefined, isSectionValid);
        }
        // Clear loading before membership fetch — the form fields are already rendered, and the
        // inline skeleton is shown alongside them (not instead of them) while isLoading is true.
        setIsLoading(false);
    };

    const handleFieldChange = (updatedFormState: HorseFormState) => {
        if (onFieldChange) {
            const isSectionValid = checkIfSectionIsValid(updatedFormState);
            onFieldChange(selectedHorseState || undefined, isSectionValid);
        }
    };

    const handleHeightChange = (h: number, f: string) => {
        const updatedFormState: HorseFormState = {
            ...formState,
            height: h,
            fraction: f,
        };
        setFormState((prev) => ({
            ...prev,
            height: h,
            fraction: f,
        }));
        handleFieldChange(updatedFormState);
    };

    const handleSexChange = (sex: string) => {
        const updatedFormState = {
            ...formState,
            sex: sex,
        };
        setFormState((prev) => ({
            ...prev,
            sex: sex,
        }));
        handleFieldChange(updatedFormState);
    };

    const handleBreedChange = (breed: string) => {
        const updatedFormState = {
            ...formState,
            breed: breed,
        };
        setFormState((prev) => ({
            ...prev,
            breed: breed,
        }));
        handleFieldChange(updatedFormState);
    };

    const checkIfFieldIsRequired = (fieldName: string) => {
        if (requiredFields && requiredFields.length > 0) {
            const index = requiredFields.findIndex((field) => field.toLocaleLowerCase() === fieldName.toLocaleLowerCase());
            if (index > -1) return true;
        }
        return false;
    };

    const checkIfSectionIsValid = (currentFormState: HorseFormState) => {
        if (checkIfFieldIsRequired("name") && currentFormState.name === "") return false;
        if (checkIfFieldIsRequired("name") && currentFormState.name === "") return false;
        if (checkIfFieldIsRequired("height") && currentFormState.height === 0) return false;
        if (checkIfFieldIsRequired("sex") && currentFormState.sex === "") return false;
        if (checkIfFieldIsRequired("breed") && currentFormState.breed === "") return false;
        return true;
    };

    /**
     * Called by the useOnClickOutside Hook
     * Checks for a horse that needs to be updated or a new name that needs to be made into a Horse
     * Updates the appropriate formattedOptions array and re-sets it in state
     * This will trigger a re-render, so that the SelectOrCreateHorse component will have the newest data
     * If the user tries clicking on that horse again, the proper value should be displayed.
     * @param horseNeedingUpdate the selected horse from SelectOrCreateHorse
     * @returns null
     */
    const handleSaveHorseData = async (horseNeedingUpdate?: Horse | null | undefined) => {
        // Use formStateRef to get the latest formState value
        const currentFormState = formStateRef.current;
        // Prevent concurrent saves
        if (isSavingRef.current) {
            return;
        }

        // If this function is called before any horse was selected or a name was selected, don't perform any actions.
        if (!horseNeedingUpdate && !currentFormState.name) {
            return;
        }

        // Do not save data if the section is not valid (all required fields must be completed)
        const isSectionValid = checkIfSectionIsValid(currentFormState);
        if (!isSectionValid) {
            return;
        }

        // Prevent duplicate creation: if we're trying to create a new horse but one with this name already exists
        if (!horseNeedingUpdate && currentFormState.name) {
            const existingHorse = selectedHorseRef.current || selectedHorseState;
            if (existingHorse && existingHorse.name === currentFormState.name) {
                // Horse already exists, don't create again
                return;
            }
        }

        // Set saving flag to prevent concurrent saves
        isSavingRef.current = true;
        if (onSavingStateChange) onSavingStateChange(true);

        // Track the updated Horse
        let updatedHorseResult: Horse | null | undefined = horseNeedingUpdate;

        // First, check if this horse already exists.
        if (horseNeedingUpdate) {
            // Next, check what actually needs to be update in order to avoid an update if the values for the horse were not changed.
            let shouldUpdateHorseInDB = false;
            let updateInput: UpdateHorseInput = {
                id: horseNeedingUpdate.id,
            };
            if (currentFormState.name && currentFormState.name !== horseNeedingUpdate.name) {
                shouldUpdateHorseInDB = true;
                updateInput["name"] = currentFormState.name;
            }
            if (currentFormState.height && currentFormState.height.toString() !== horseNeedingUpdate.height) {
                shouldUpdateHorseInDB = true;
                updateInput["height"] = currentFormState.height.toString();
            }
            if (currentFormState.fraction && currentFormState.fraction !== horseNeedingUpdate.fraction) {
                shouldUpdateHorseInDB = true;
                updateInput["fraction"] = currentFormState.fraction;
            }
            if (currentFormState.breed && currentFormState.breed !== horseNeedingUpdate.breed) {
                shouldUpdateHorseInDB = true;
                updateInput["breed"] = currentFormState.breed;
            }
            if (currentFormState.sex && currentFormState.sex !== horseNeedingUpdate.sex) {
                shouldUpdateHorseInDB = true;
                updateInput["sex"] = currentFormState.sex;
            }
            if (shouldUpdateHorseInDB) {
                // Run the following command to update the horse in the DB
                const updateResult = await updateHorse(updateInput);
                if (updateResult.isSuccess) {
                    const updatedHorse: Horse = updateResult.result;
                    updatedHorseResult = updatedHorse;
                    const formattedOption = await formatSingleHorse(updatedHorse);

                    // Find the group that this horse is in
                    const userGroupIndex = userHorseOptions?.findIndex((h) => h.value === updatedHorse.id);
                    const barnGroupIndex = barnHorseOptions?.findIndex((h) => h.value === updatedHorse.id);

                    // Update the appropriate group
                    if (barnGroupIndex !== undefined && barnGroupIndex > -1 && barnHorseOptions) {
                        const updatedBarnGroup: formattedOption[] = [
                            ...barnHorseOptions?.slice(0, barnGroupIndex),
                            formattedOption,
                            ...barnHorseOptions?.slice(barnGroupIndex + 1),
                        ];
                        setBarnHorseOptions(updatedBarnGroup);
                    } else if (userGroupIndex !== undefined && userGroupIndex > -1 && userHorseOptions) {
                        const updatedUserGroup: formattedOption[] = [
                            ...userHorseOptions?.slice(0, userGroupIndex),
                            formattedOption,
                            ...userHorseOptions?.slice(userGroupIndex + 1),
                        ];
                        setUserHorseOptions(updatedUserGroup);
                    } else {
                        // Could not find the horse in either list
                    }
                } else {
                    // TODO: show error
                }
            }
        }

        // OTW, check if this is a new horse that needs to be created.
        else if (currentFormState.name) {
            const createInput: CreateHorseInput = {
                name: currentFormState.name,
                height: currentFormState.height.toString() || "",
                fraction: currentFormState.fraction || "",
                breed: currentFormState.breed || "",
                sex: currentFormState.sex || "",
                barnId: barn?.id || null,
                barnName: barn?.name || "",
                ownerName: "",
                personId: user.id,
            };
            const createResult = await createHorse(createInput);
            if (createResult.isSuccess) {
                const newHorse: Horse = createResult.result;
                updatedHorseResult = newHorse;

                // IMMEDIATELY update the ref to prevent duplicate creation
                // This must happen before any other async operations
                selectedHorseRef.current = newHorse;

                // Format the horse for the dropdown
                const formattedOption = await formatSingleHorse(newHorse, user);

                // Determine which list to add the horse to based on barn
                // Use the barn we passed in createInput rather than relying on database response
                // This ensures we use the correct barn even if the response hasn't updated yet
                const horseBelongsToCurrentBarn = barn && (newHorse.barnId === barn.id || createInput.barnId === barn.id);

                // Update state in a way that ensures React detects the change
                if (horseBelongsToCurrentBarn) {
                    // Horse belongs to this barn - add to barnHorseOptions
                    setBarnHorseOptions((prevBarnHorses) => {
                        // Check if already in list to prevent duplicates
                        const isAlreadyInList = prevBarnHorses?.some((h) => h.value === newHorse.id);
                        if (isAlreadyInList) {
                            return prevBarnHorses;
                        }
                        // Always create a new array to ensure React detects the change
                        const baseArray = prevBarnHorses || [];
                        const updatedBarnHorses: formattedOption[] = [...baseArray, formattedOption];
                        // Sort alphabetically by horse name
                        const sorted = updatedBarnHorses.sort((a, b) => a.horseName.trim().localeCompare(b.horseName.trim()));
                        // Force a new array reference even after sorting
                        const finalArray = [...sorted];
                        return finalArray;
                    });

                    // Remove from userHorseOptions if it exists there (to avoid duplicates)
                    setUserHorseOptions((prevUserHorses) => {
                        if (prevUserHorses && prevUserHorses.length > 0) {
                            const filtered = prevUserHorses.filter((h) => h.value !== newHorse.id);
                            return filtered;
                        }
                        return prevUserHorses;
                    });
                } else {
                    // Horse doesn't belong to a barn or belongs to a different barn - add to userHorseOptions
                    setUserHorseOptions((prevUserHorses) => {
                        // Check if already in list to prevent duplicates
                        const isAlreadyInList = prevUserHorses?.some((h) => h.value === newHorse.id);
                        if (isAlreadyInList) {
                            return prevUserHorses;
                        }
                        // Always create a new array to ensure React detects the change
                        const baseArray = prevUserHorses || [];
                        const updatedUserHorses: formattedOption[] = [...baseArray, formattedOption];
                        const sorted = updatedUserHorses.sort((a, b) => a.horseName.trim().localeCompare(b.horseName.trim()));
                        return sorted;
                    });

                    // Remove from barnHorseOptions if it exists there
                    setBarnHorseOptions((prevBarnHorses) => {
                        if (prevBarnHorses && prevBarnHorses.length > 0) {
                            const filtered = prevBarnHorses.filter((h) => h.value !== newHorse.id);
                            return filtered;
                        }
                        return prevBarnHorses;
                    });
                }

                // Update selectedHorseState after options are updated to ensure dropdown shows the horse
                setSelectedHorseState(newHorse);

                // Update form state with the newly created horse's data
                const updatedFormState: HorseFormState = {
                    name: newHorse.name || currentFormState.name,
                    height: newHorse.height ? parseFloat(newHorse.height) : currentFormState.height,
                    fraction: newHorse.fraction || currentFormState.fraction,
                    sex: newHorse.sex || currentFormState.sex,
                    breed: newHorse.breed || currentFormState.breed,
                };
                setFormState(updatedFormState);
                formStateRef.current = updatedFormState; // Update ref immediately

                // Notify parent component
                const isSectionValid = checkIfSectionIsValid(updatedFormState);
                if (onSelectHorse) {
                    onSelectHorse(newHorse, isSectionValid);
                }
                if (onFieldChange) {
                    onFieldChange(newHorse, isSectionValid);
                }
                // Reset the saved name ref since we successfully created the horse
                lastSavedHorseNameRef.current = "";
            } else {
                console.error("[SelectHorseSection] createHorse failed - isSuccess:", createResult.isSuccess, "message:", createResult.message, "result:", createResult.result);
                // Reset the saved name ref on failure so user can retry
                lastSavedHorseNameRef.current = "";
            }
        }

        // For updates (not new horses), we need to update selectedHorseState
        if (updatedHorseResult && horseNeedingUpdate) {
            setSelectedHorseState(updatedHorseResult);
        }

        // Reset saving flag
        isSavingRef.current = false;
        if (onSavingStateChange) onSavingStateChange(false);
    };

    return (
        <>
            <div data-section="horse">
                {/* Pass the ref as the ref attribute to the highest level IonRow tag - this is where we want to get the DOM node */}
                <IonRow ref={horseComponentRef}>
                    <IonCol sizeXs="12" sizeMd={formState.name ? "6" : "12"} className={`${isWindows() || isPlatform("desktop") ? "" : "pb-0"}`}>
                        <Label>
                            Name
                            {checkIfFieldIsRequired("name") && <RequiredInputIndicator />}
                        </Label>
                        <SelectOrCreateHorse
                            key={`horse-select-${barn?.id || "no-barn"}-${barnHorseOptions?.length || 0}-${userHorseOptions?.length || 0}`}
                            userHorseOptions={userHorseOptions}
                            barnHorseOptions={barnHorseOptions}
                            selectedBarn={barn}
                            selectedValue={selectedHorseState?.id}
                            onSelect={(horse?: Horse, horseName?: string) => {
                                handleSelectedHorse(horse, horseName);
                            }}
                        />
                    </IonCol>
                    {/* Show the SelectOrCreateHorse input always, but only show the other inputs once a horse name has been selected */}
                    {/* Note - best to not dynamically hide the inputs based on isLoading. It will cause issues with loading data for the DOM */}
                    {formState.name && (
                        <>
                            <IonCol sizeXs="12" sizeMd="6" className={`${isWindows() || isPlatform("desktop") ? "" : "pt-0"}`}>
                                <HorseHeightInput
                                    isRequired={checkIfFieldIsRequired("height")}
                                    heightInInches={formState.height || 0}
                                    fraction={formState.fraction || ""}
                                    onChange={(h: number, f: string) => {
                                        handleHeightChange(h, f);
                                    }}
                                />
                            </IonCol>
                            <IonCol sizeXs="12" sizeMd="6">
                                <Label for="sexSelect">
                                    Sex
                                    {checkIfFieldIsRequired("sex") && <RequiredInputIndicator />}
                                </Label>
                                <Input
                                    type="select"
                                    name="select"
                                    value={formState.sex}
                                    id="sexSelect"
                                    onChange={(e) => {
                                        handleSexChange(e.target.value);
                                    }}>
                                    <option>-</option>
                                    <option>Gelding</option>
                                    <option>Mare</option>
                                    <option>Stallion</option>
                                    <option>Colt</option>
                                </Input>
                            </IonCol>
                            <IonCol sizeXs="12" sizeMd="6">
                                <Label for="breedSelect">
                                    Breed
                                    {checkIfFieldIsRequired("breed") && <RequiredInputIndicator />}
                                </Label>
                                <Input
                                    type="select"
                                    name="select"
                                    value={formState.breed}
                                    id="breedSelect"
                                    onChange={(e) => {
                                        handleBreedChange(e.target.value);
                                    }}>
                                    <option>-</option>
                                    {breeds.map((breed, index) => (
                                        <option key={index}>{breed}</option>
                                    ))}
                                </Input>
                            </IonCol>
                            <MembershipUI
                                entityType="horse"
                                entityId={selectedHorseState?.id}
                                disabled={!formState.name}
                                ensureEntityExists={async () => {
                                    if (!selectedHorseState?.id && formState.name && checkIfSectionIsValid(formState)) {
                                        await handleSaveHorseData(selectedHorseRef.current);
                                    }
                                    return selectedHorseRef.current?.id;
                                }}
                            />
                        </>
                    )}
                    {/* Only show the progressive-load skeleton before horse detail fields exist.
                        Once name/sex/breed are rendered, isLoading (barn list fetch, etc.) must not
                        paint a second set of placeholder fields underneath the real UI. */}
                    {isLoading && !formState.name && <FormFieldsInlineSkeleton fieldCount={1} />}
                </IonRow>
            </div>
        </>
    );
};

export default SelectHorseSection;
